import React, { useState, useEffect, useRef, KeyboardEvent, ChangeEvent } from 'react';
import { Input, Tag, Spin } from 'antd';
import { BulbOutlined, LoadingOutlined } from '@ant-design/icons';
import { getWritingSuggestion, getSuggestionOptions } from '../../Services/aiService';
import './AITextArea.css';

const { TextArea } = Input;

interface AITextAreaProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  rows?: number;
  maxLength?: number;
  disabled?: boolean;
  style?: React.CSSProperties;
  context?: {
    nomineeNames?: string | string[];
    selectedValue?: string;
    awardType?: string;
    contributionTowards?: string[];
    businessOutcome?: string;
    isTeamAward?: boolean;
  };
  fetchToken: () => Promise<string>;
  minLength?: number;
  enableAutoSuggest?: boolean;
}

/**
 * Smart TextArea with AI-powered auto-completion
 * Shows inline suggestions as grayed-out text (like GitHub Copilot)
 * Press Tab to accept, Escape to dismiss
 */
const AITextArea: React.FC<AITextAreaProps> = ({
  value = '',
  onChange,
  placeholder,
  rows = 4,
  maxLength = 1000,
  disabled = false,
  style,
  context,
  fetchToken,
  minLength = 0,
  enableAutoSuggest = true,
}) => {
  const [localValue, setLocalValue] = useState(value);
  const [suggestion, setSuggestion] = useState('');
  const [suggestionOptions, setSuggestionOptions] = useState<string[]>([]);
  const [isLoadingSuggestion, setIsLoadingSuggestion] = useState(false);
  const [showSuggestionMenu, setShowSuggestionMenu] = useState(false);
  const textAreaRef = useRef<any>(null);
  const suggestionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [cursorPosition, setCursorPosition] = useState(0);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  // Debounced suggestion fetching
  const fetchSuggestion = async (text: string) => {
    if (!enableAutoSuggest || !text || text.length < 20 || disabled) {
      setSuggestion('');
      return;
    }

    // Only suggest if user stopped typing for 800ms
    if (suggestionTimeoutRef.current) {
      clearTimeout(suggestionTimeoutRef.current);
    }

    suggestionTimeoutRef.current = setTimeout(async () => {
      setIsLoadingSuggestion(true);
      try {
        const token = await fetchToken();
        const aiSuggestion = await getWritingSuggestion(
          {
            currentText: text,
            context: context || {},
          },
          token
        );
        
        if (aiSuggestion && aiSuggestion.trim()) {
          setSuggestion(aiSuggestion);
        }
      } catch (error) {
        console.debug('Failed to fetch suggestion:', error);
      } finally {
        setIsLoadingSuggestion(false);
      }
    }, 800);
  };

  // Fetch multiple suggestion options
  const fetchSuggestionOptions = async (text: string) => {
    if (!text || text.length < 20) return;

    try {
      const token = await fetchToken();
      const options = await getSuggestionOptions(
        {
          currentText: text,
          context: context || {},
          maxSuggestions: 3,
        },
        token
      );
      setSuggestionOptions(options);
      setShowSuggestionMenu(options.length > 0);
    } catch (error) {
      console.debug('Failed to fetch suggestion options:', error);
    }
  };

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
    setCursorPosition(e.target.selectionStart || 0);
    
    if (onChange) {
      onChange(newValue);
    }

    // Clear current suggestion
    setSuggestion('');
    setShowSuggestionMenu(false);

    // Fetch new suggestion
    fetchSuggestion(newValue);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Tab: Accept inline suggestion
    if (e.key === 'Tab' && suggestion && !showSuggestionMenu) {
      e.preventDefault();
      const newValue = localValue + suggestion;
      setLocalValue(newValue);
      setSuggestion('');
      if (onChange) {
        onChange(newValue);
      }
    }

    // Escape: Dismiss suggestion
    if (e.key === 'Escape') {
      setSuggestion('');
      setShowSuggestionMenu(false);
    }

    // Ctrl+Space: Show suggestion menu
    if (e.ctrlKey && e.key === ' ') {
      e.preventDefault();
      fetchSuggestionOptions(localValue);
    }
  };

  const acceptSuggestionOption = (option: string) => {
    const newValue = localValue + option;
    setLocalValue(newValue);
    setShowSuggestionMenu(false);
    setSuggestionOptions([]);
    if (onChange) {
      onChange(newValue);
    }
    
    // Focus back to textarea
    if (textAreaRef.current) {
      textAreaRef.current.focus();
    }
  };

  const charactersRemaining = maxLength - localValue.length;
  const meetsMinimum = !minLength || localValue.length >= minLength;

  return (
    <div className="ai-textarea-wrapper" style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        {/* Main TextArea */}
        <TextArea
          ref={textAreaRef}
          value={localValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          rows={rows}
          maxLength={maxLength}
          disabled={disabled}
          style={{
            ...style,
            resize: 'none',
            fontFamily: 'monospace',
          }}
        />

        {/* Inline AI Suggestion (grayed-out text) */}
        {suggestion && !showSuggestionMenu && (
          <div
            className="ai-inline-suggestion"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              pointerEvents: 'none',
              padding: '4px 11px',
              fontFamily: 'monospace',
              fontSize: '14px',
              lineHeight: '1.5715',
              whiteSpace: 'pre-wrap',
              wordWrap: 'break-word',
              color: 'transparent',
            }}
          >
            <span style={{ visibility: 'hidden' }}>{localValue}</span>
            <span style={{ color: '#bbb', visibility: 'visible' }}>{suggestion}</span>
          </div>
        )}

        {/* Loading indicator */}
        {isLoadingSuggestion && (
          <div
            style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              pointerEvents: 'none',
            }}
          >
            <Spin indicator={<LoadingOutlined style={{ fontSize: 16 }} spin />} />
          </div>
        )}
      </div>

      {/* Suggestion Menu (Ctrl+Space) */}
      {showSuggestionMenu && suggestionOptions.length > 0 && (
        <div
          className="ai-suggestion-menu"
          style={{
            marginTop: '8px',
            padding: '12px',
            backgroundColor: '#fff',
            border: '1px solid #d9d9d9',
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          }}
        >
          <div style={{ marginBottom: '8px', fontSize: '12px', color: '#888' }}>
            <BulbOutlined /> AI Suggestions (click to use):
          </div>
          {suggestionOptions.map((option, index) => (
            <Tag
              key={index}
              style={{
                cursor: 'pointer',
                marginBottom: '8px',
                padding: '8px 12px',
                fontSize: '14px',
                display: 'block',
                whiteSpace: 'normal',
                height: 'auto',
              }}
              onClick={() => acceptSuggestionOption(option)}
            >
              {option}
            </Tag>
          ))}
        </div>
      )}

      {/* Helper text */}
      <div
        style={{
          marginTop: '4px',
          fontSize: '12px',
          color: '#888',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div>
          {suggestion && (
            <span>
              <BulbOutlined style={{ marginRight: '4px' }} />
              Press <kbd>Tab</kbd> to accept suggestion, <kbd>Esc</kbd> to dismiss
            </span>
          )}
          {!suggestion && enableAutoSuggest && localValue.length >= 20 && (
            <span>
              <BulbOutlined style={{ marginRight: '4px' }} />
              Press <kbd>Ctrl+Space</kbd> for suggestions
            </span>
          )}
        </div>
        <div>
          <span style={{ color: meetsMinimum ? '#52c41a' : '#ff4d4f' }}>
            {localValue.length}
            {minLength > 0 && ` / ${minLength}`}
          </span>
          {' / '}
          <span>{maxLength}</span>
        </div>
      </div>
    </div>
  );
};

export default AITextArea;
